import {FC, useCallback, useMemo, useState} from 'react';
//mui
import {Box, SxProps, Theme} from '@mui/material';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePickerProps, DateValidationError, PickerChangeHandlerContext} from '@mui/x-date-pickers';
import {DesktopDatePicker as MuiDatePicker} from '@mui/x-date-pickers/DesktopDatePicker';
//dayjs
import dayjs, {Dayjs} from 'dayjs';
//formik
import {FieldInputProps, FormikProps} from 'formik';
//icons
import {ReactComponent as CalendarIcon} from '@icons/calendar.svg';
//constants
import {DAY, MM_DD_YYYY_SLASH, OUTLINED} from '@/utils/constants';
//components
import InputLabel from '../Inputs/InputLabel';
import FormHelperText from '../FormHelperText';
//styles
import {datePickerStyle} from './DatePicker.styles';

interface CustomDatePickerProps {
	form: FormikProps<any>;
	field: FieldInputProps<string | number>;
	label?: string;
	sx?: SxProps<Theme>;
	parentClassName?: SxProps<Theme>;
	format?: string;
	disableErrorMessage?: boolean;
	//defaultValue?: DatePickerProps<Dayjs>;
	defaultValue?: Dayjs | null;
	disabled?: boolean;
}

const DatePicker: FC<CustomDatePickerProps> = ({
	form,
	field,
	label,
	format,
	sx,
	parentClassName,
	disableErrorMessage,
	defaultValue,
	disabled,
	...rest
}) => {
	const [isDateValid, setIsDateValid] = useState(true);
	const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);

	// Allow consumers (Formik <Field /> usage) to pass an onChange handler; MUI's DatePicker doesn't
	// automatically wire that through when we override onChange.
	const {onChange: externalOnChange, ...restProps} = rest as Partial<DatePickerProps<Dayjs>> & {
		onChange?: (value: Dayjs | null, context: PickerChangeHandlerContext<DateValidationError>) => void;
	};

	const handleDatePickerVisible = () => setIsDatePickerVisible(!isDatePickerVisible);

	const errorText = useMemo(() => {
		return (field?.name && form?.touched[field?.name] && (form?.errors[field?.name] as string)) || undefined;
	}, [form?.errors, form?.touched, field]);

	const isError = useMemo(() => !!errorText || !isDateValid, [errorText, isDateValid]);

	const changeHandler = (event: Dayjs | null, context: PickerChangeHandlerContext<DateValidationError>) => {
		if (context.validationError) {
			setIsDateValid(false);
			return;
		}

		form.setFieldValue(field.name, dayjs(event as unknown as Dayjs).format(MM_DD_YYYY_SLASH));
		form.setFieldTouched(field.name, true, false);
		externalOnChange?.(event, context);
		setIsDateValid(true);
	};

	const openPickerIcon = useCallback(
		() => (
			<Box
				sx={{
					...(isError && datePickerStyle.iconError),
					...(disabled && datePickerStyle.iconDisabled),
				}}
			>
				<CalendarIcon />
			</Box>
		),
		[isError, disabled]
	);

	return (
		<Box sx={{...parentClassName, ...datePickerStyle.wrapper}}>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				{label && <InputLabel error={isError} label={label} disabled={disabled} />}

				{/* <MuiDatePicker */}
				<MuiDatePicker<Dayjs>
					{...restProps}
					sx={{
						...sx,
						...datePickerStyle.picker,
						...(!isError && datePickerStyle.pickerValid),
						...(isDatePickerVisible && datePickerStyle.pickerActive),
					}}
					format={format || MM_DD_YYYY_SLASH}
					onChange={changeHandler}
					defaultValue={defaultValue}
					value={field.value ? dayjs(field.value, format || MM_DD_YYYY_SLASH) : null}
					onOpen={handleDatePickerVisible}
					onClose={handleDatePickerVisible}
					views={[DAY]}
					disabled={disabled}
					showDaysOutsideCurrentMonth
					slotProps={{
						textField: {
							variant: OUTLINED,
							error: isError,
						},
					}}
					slots={{
						openPickerIcon,
					}}
				/>
			</LocalizationProvider>

			{!disableErrorMessage && errorText && <FormHelperText error title={errorText} />}
		</Box>
	);
};

export default DatePicker;
